/**
 * purchaseTax.ts — split input tax on a purchase line into its GST components.
 *
 * This existed twice, differently. The direct-receive path put the whole tax
 * into IGST; the draft-PO path split it 50/50 into CGST/SGST. Neither consulted
 * the supplier's state, so the tax head on an inward supply was decided by which
 * screen the user happened to open.
 *
 * Those columns feed the GSTR-2B reconciliation, so a correctly-filed supplier
 * invoice showed up as a component mismatch; they also feed the Tally export and
 * the GSTR-3B Table 4 input-credit claim.
 */

export interface PurchaseTaxSplit {
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
}

const toRupees = (paisa: number): number => Number((paisa / 100).toFixed(2));

/**
 * Split `lineTotal × taxRate%` across the right heads.
 *
 * Inter-state → IGST. Intra-state → CGST + SGST.
 *
 * The halving is done in integer paisa, matching `billingEngine`: rounding two
 * halves independently can make them sum to more than the tax (₹0.05 halved and
 * rounded twice is ₹0.06). SGST takes the floor and CGST absorbs the odd paisa,
 * so `cgst + sgst` always equals the tax exactly.
 */
export function computePurchaseTax(
  lineTotal: number,
  taxRate: number,
  isInterState: boolean,
): PurchaseTaxSplit {
  const taxPaisa = Math.round(lineTotal * taxRate); // (total × rate/100) × 100

  if (isInterState) {
    return { cgstAmount: 0, sgstAmount: 0, igstAmount: toRupees(taxPaisa) };
  }

  const sgstPaisa = Math.floor(taxPaisa / 2);
  const cgstPaisa = taxPaisa - sgstPaisa;

  return { cgstAmount: toRupees(cgstPaisa), sgstAmount: toRupees(sgstPaisa), igstAmount: 0 };
}
